import type { Rarity } from "@/domain/gems-game/domain/lib/Earning";
import type { UserStatsResponse } from "@/domain/gems-game/domain/lib/Sweepstake";
import { RARITY_LEVELS } from "@/utils/rewards";
import { onDarkBase } from "@biom3/design-tokens";
import {
  Box,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import { ProgressBarDropdownContent } from "./ProgressBarDropdown/ProgressBarDropdownContent";

interface Props {
  userStats?: UserStatsResponse;
  currentRarity: Rarity;
  hideTrigger?: boolean;
  anchorPosition?: { top: number; left: number } | null;
  open?: boolean;
  onCloseExtern?: () => void;
}

export const WeeklyRewardsInfoDropdown = ({
  userStats,
  currentRarity,
  anchorPosition: controlledAnchorPosition,
  open: controlledOpen,
  onCloseExtern,
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const previousRarity = useMemo(() => {
    if (!currentRarity) return "Common" as Rarity;
    const currentIndex = RARITY_LEVELS.indexOf(currentRarity);
    if (currentIndex <= 0) return "Common" as Rarity;
    return RARITY_LEVELS[currentIndex - 1] as Rarity;
  }, [currentRarity]);

  const anchorPosition = controlledAnchorPosition;
  const open = Boolean(controlledOpen);

  const handleClose = () => {
    onCloseExtern?.();
  };

  return (
    <Popover
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        paper: {
          elevation: 8,
          sx: {
            backgroundColor: onDarkBase.color.neutral[800],
            border: `1px solid ${onDarkBase.color.neutral[600]}`,
            maxWidth: isMobile ? "calc(100% - 32px)" : "480px",
            maxHeight: isMobile ? "90vh" : "80vh",
            overflow: "auto",
          },
        },
      }}
    >
      <Box sx={{ p: onDarkBase.spacing.x6 }}>
        {userStats ? (
          <ProgressBarDropdownContent
            userStats={userStats}
            rarity={currentRarity}
            onClose={handleClose}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: onDarkBase.color.text.body.secondary,
              fontStyle: "italic",
            }}
          >
            Loading quest information...
          </Typography>
        )}
      </Box>
    </Popover>
  );
};
